import { useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import InfoTooltip from "../components/common/InfoTooltip";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import {
  getSuperAdminAppointmentStatusFilters,
  postSuperAdminAppointmentStatusReport,
} from "../api/superadminReports";
import { formatInTimeZone } from "../utils/datetime";
import { BTN_PRIMARY, FIELD_CLASS, LABEL_TEXT, MODAL_TEXT } from "../ui/modalStyles";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const TOOLTIP_MAX_WIDTH = 240;
const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];
const DEFAULT_STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "checked_in", label: "Checked-in" },
  { value: "confirmed", label: "Confirmed" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];
const DEFAULT_GROUP_BY_OPTIONS = [
  { value: "status", label: "Status" },
  { value: "employee", label: "Employee" },
  { value: "day_of_week", label: "Day of week" },
  { value: "time_of_day", label: "Time of day" },
];
const DEFAULT_BUCKET_OPTIONS = [15, 30, 60];
const FIELD_TOOLTIPS = {
  scope: "Run across all salons, salons under one business, or one salon.",
  business_scope: "Limits results to salons under the selected business.",
  salon_scope: "Limits results to one salon.",
  bucket: "Groups time-of-day results into 15, 30, or 60 minute buckets.",
  show_scope_columns: "Shows Business and Salon columns in report tables.",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "appointments-status",
  title: "Appointment Status Report (Super Admin)",
  subtitle:
    "Track appointment status counts and trends across the selected scope.",
  summaryLabel: "Status Summary",
  detailsLabel: "Status Breakdown",
  hasExport: false,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("appointments-status");

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(value);
    } catch (err) {
      // Fall back to local time when the timezone is invalid.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatStatusLabel = (value, options) => {
  const match = (options || []).find((opt) => opt.value === value);
  if (match) return match.label;
  const text = String(value || "").replace(/_/g, " ").trim();
  if (!text) return "";
  return text
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatEntityLabelWithId = (idValue, nameValue) => {
  const hasId = idValue !== null && idValue !== undefined && idValue !== "";
  const idText = hasId ? String(idValue) : "";
  const nameText = String(nameValue || "").trim();
  if (idText && nameText) return `${idText} - ${nameText}`;
  if (idText) return idText;
  if (nameText) return nameText;
  return "--";
};

export default function SuperAdminAppointmentsStatus() {
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");
  const [showScopeColumns, setShowScopeColumns] = useState(true);

  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
  const [groupByOptions, setGroupByOptions] = useState(DEFAULT_GROUP_BY_OPTIONS);
  const [bucketOptions, setBucketOptions] = useState(DEFAULT_BUCKET_OPTIONS);

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((item) => {
      if (!item) return;
      map.set(String(item.id), item);
    });
    return map;
  }, [salons]);

  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const salon = salonMap.get(String(salonId));
      if (salon?.timezone) return salon.timezone;
    }
    if (businessId) {
      const business = businessMap.get(String(businessId));
      if (business?.timezone) return business.timezone;
    }
    return authSalonTimezone || "UTC";
  }, [salonId, salonMap, businessId, businessMap, authSalonTimezone]);

  const today = useMemo(() => formatDateISO(new Date(), anchorTimezone), [anchorTimezone]);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [groupBy, setGroupBy] = useState("status");
  const [bucketMinutes, setBucketMinutes] = useState(60);
  const [statuses, setStatuses] = useState(
    DEFAULT_STATUS_OPTIONS.filter((opt) => opt.value !== "cancelled").map(
      (opt) => opt.value
    )
  );
  const [employeeId, setEmployeeId] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState("");
  const [loading, setLoading] = useState(false);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (item) => String(item.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  const broadScope = scope === "all" || scope === "business";

  useEffect(() => {
    setStartDate(today);
    setEndDate(today);
  }, [today]);

  useEffect(() => {
    if (!broadScope) {
      setShowScopeColumns(false);
      return;
    }
    setShowScopeColumns(true);
  }, [broadScope]);

  useEffect(() => {
    if (!is_super_admin) return;
    let mounted = true;

    const loadFilters = async () => {
      setFiltersLoading(true);
      setFiltersError("");
      try {
        const data = await getSuperAdminAppointmentStatusFilters({
          scope,
          business_id: businessId || undefined,
          salon_id: salonId || undefined,
        });
        if (!mounted) return;
        setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
        setSalons(Array.isArray(data?.salons) ? data.salons : []);
        setEmployees(Array.isArray(data?.employees) ? data.employees : []);
        setGroupByOptions(
          Array.isArray(data?.group_by_options) && data.group_by_options.length
            ? data.group_by_options
            : DEFAULT_GROUP_BY_OPTIONS
        );
        setBucketOptions(
          Array.isArray(data?.bucket_options) && data.bucket_options.length
            ? data.bucket_options
            : DEFAULT_BUCKET_OPTIONS
        );
        const nextStatusOptions =
          Array.isArray(data?.status_options) && data.status_options.length
            ? data.status_options
            : DEFAULT_STATUS_OPTIONS;
        setStatusOptions(nextStatusOptions);
        setStatuses((prev) => {
          const allowed = new Set(nextStatusOptions.map((row) => row.value));
          const filtered = (prev || []).filter((value) => allowed.has(value));
          if (filtered.length) return filtered;
          const defaults = nextStatusOptions
            .map((row) => row.value)
            .filter((value) => value !== "cancelled");
          return defaults.length ? defaults : nextStatusOptions.map((row) => row.value);
        });
      } catch (error) {
        if (!mounted) return;
        setBusinesses([]);
        setSalons([]);
        setEmployees([]);
        setFiltersError(error?.message || "Unable to load scope filters.");
      } finally {
        if (mounted) setFiltersLoading(false);
      }
    };

    loadFilters();
    return () => {
      mounted = false;
    };
  }, [is_super_admin, scope, businessId, salonId]);

  useEffect(() => {
    if (employeeId && !(employees || []).some((row) => String(row.id) === String(employeeId))) {
      setEmployeeId("");
    }
  }, [employeeId, employees]);

  const handleScopeChange = (value) => {
    setScope(value);
    if (value === "all") {
      setSalonId("");
      return;
    }
    if (value === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!value && scope !== "salon") {
      setSalonId("");
      return;
    }
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (
        selectedSalon &&
        value &&
        String(selectedSalon.business_id || "") !== String(value)
      ) {
        setSalonId("");
      }
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const toggleStatus = (value) => {
    setStatuses((prev) => {
      if (prev.includes(value)) return prev.filter((status) => status !== value);
      return [...prev, value];
    });
  };

  const isInvalidDateRange = useMemo(
    () => startDate && endDate && startDate > endDate,
    [startDate, endDate]
  );
  const isTimePairIncomplete = useMemo(
    () => (startTime && !endTime) || (!startTime && endTime),
    [startTime, endTime]
  );
  const isInvalidTimeRange = useMemo(
    () => startTime && endTime && startTime > endTime,
    [startTime, endTime]
  );

  const handleRunReport = async () => {
    if (scope === "business" && !businessId) {
      setReportError("Choose a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setReportError("Choose a salon for Salon scope.");
      return;
    }
    if (isInvalidDateRange) {
      setReportError("End date can't be before start date.");
      return;
    }
    if (isTimePairIncomplete) {
      setReportError("Start time and end time are required.");
      return;
    }
    if (isInvalidTimeRange) {
      setReportError("Start time must be earlier than end time.");
      return;
    }
    if (!statuses.length) {
      setReportError("Select at least one status.");
      return;
    }

    setReportError("");
    setLoading(true);
    try {
      const payload = {
        scope,
        business_id: businessId ? Number(businessId) : undefined,
        salon_id: salonId ? Number(salonId) : undefined,
        date_from: startDate,
        date_to: endDate,
        statuses,
        group_by: groupBy,
        sort_dir: "asc",
        bucket_minutes: bucketMinutes,
        employee_id: employeeId ? Number(employeeId) : null,
        include_business_salon_cols: broadScope && showScopeColumns,
      };
      if (startTime && endTime) {
        payload.start_time = startTime;
        payload.end_time = endTime;
      }
      const data = await postSuperAdminAppointmentStatusReport(payload);
      setReportData(data);
    } catch (err) {
      setReportError(err?.message || "Couldn't load appointments. Please try again.");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const groups = reportData?.groups || [];
  const metaTimezone = reportData?.meta?.timezone;
  const reportScope = reportData?.meta?.scope || scope;
  const salonTimezoneLabel =
    metaTimezone && metaTimezone !== "per-salon" ? metaTimezone : anchorTimezone;
  const timezoneNote =
    reportScope === "salon"
      ? `Times shown in ${salonTimezoneLabel}.`
      : "Times shown in each salon's local timezone.";

  const withScopeColumns = broadScope && showScopeColumns;

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Appointment Status Report" closeTo="/superadmin/reports" />
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-6 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex flex-col gap-1">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Scope</label>
                    <InfoTooltip text={FIELD_TOOLTIPS.scope} label="Scope" maxWidth={TOOLTIP_MAX_WIDTH} />
                  </div>
                  <select
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={filtersLoading}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Business</label>
                    <InfoTooltip text={FIELD_TOOLTIPS.business_scope} label="Business" maxWidth={TOOLTIP_MAX_WIDTH} />
                  </div>
                  <select
                    value={businessId}
                    onChange={(event) => handleBusinessChange(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={filtersLoading}
                  >
                    <option value="">All Businesses</option>
                    {(businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Salon</label>
                    <InfoTooltip text={FIELD_TOOLTIPS.salon_scope} label="Salon" maxWidth={TOOLTIP_MAX_WIDTH} />
                  </div>
                  <select
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={
                      filtersLoading ||
                      !(scope === "salon" || scope === "business" || Boolean(businessId))
                    }
                  >
                    <option value="">{scope === "salon" ? "Select Salon" : "All Salons"}</option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name} (ID {salon.id})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {broadScope ? (
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={showScopeColumns}
                    onChange={(event) => setShowScopeColumns(event.target.checked)}
                  />
                  <span>Show Business/Salon columns</span>
                  <InfoTooltip
                    text={FIELD_TOOLTIPS.show_scope_columns}
                    label="Show Business/Salon columns"
                    maxWidth={TOOLTIP_MAX_WIDTH}
                  />
                </label>
              ) : null}

              {filtersError ? (
                <div className="text-sm text-rose-600">{filtersError}</div>
              ) : null}

              <div className="flex flex-wrap items-end gap-4">
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Date From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Date To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Start Time</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(event) => setStartTime(event.target.value)}
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>End Time</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(event) => setEndTime(event.target.value)}
                    className={FIELD_CLASS}
                  />
                  {isTimePairIncomplete || isInvalidTimeRange ? (
                    <div className="text-sm text-rose-600">
                      {isTimePairIncomplete
                        ? "Start time and end time are required."
                        : "Start time must be earlier than end time."}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1 min-w-[180px]">
                  <label className={LABEL_TEXT}>Group By</label>
                  <select
                    value={groupBy}
                    onChange={(event) => setGroupBy(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    {groupByOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1 min-w-[140px]">
                  <div className="inline-flex items-center gap-2">
                    <label className={LABEL_TEXT}>Bucket</label>
                    <InfoTooltip text={FIELD_TOOLTIPS.bucket} label="Bucket" maxWidth={TOOLTIP_MAX_WIDTH} />
                  </div>
                  <select
                    value={bucketMinutes}
                    onChange={(event) => setBucketMinutes(Number(event.target.value))}
                    className={FIELD_CLASS}
                    disabled={groupBy !== "time_of_day"}
                  >
                    {(bucketOptions || DEFAULT_BUCKET_OPTIONS).map((option) => (
                      <option key={option} value={option}>
                        {option} min
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1 min-w-[220px]">
                  <label className={LABEL_TEXT}>Employee</label>
                  <select
                    value={employeeId}
                    onChange={(event) => setEmployeeId(event.target.value)}
                    className={FIELD_CLASS}
                    disabled={filtersLoading}
                  >
                    <option value="">All employees</option>
                    {employees.map((member) => (
                      <option key={member.id} value={String(member.id)}>
                        {member.display_name || `Staff ${member.id}`}
                        {broadScope && member.salon_name ? ` (${member.salon_name})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1 min-w-[280px]">
                  <label className={LABEL_TEXT}>Status</label>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map((status) => (
                      <label
                        key={status.value}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={statuses.includes(status.value)}
                          onChange={() => toggleStatus(status.value)}
                        />
                        {status.label}
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRunReport}
                  className={BTN_PRIMARY}
                  disabled={loading}
                >
                  {loading ? "Running..." : "Run Report"}
                </button>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.export}
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                HELP_ANCHORS.export
              )}`}
            >
              <div className="text-base font-semibold text-slate-900">Export</div>
              <div className="mt-1 text-sm text-slate-600">
                This report does not support export.
              </div>
            </div>

            {reportError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {reportError}
              </div>
            ) : null}

            {reportData ? (
              <div
                id={HELP_ANCHORS.kpis}
                className={`space-y-4 ${getSectionClassName(HELP_ANCHORS.kpis)}`}
              >
                <div className="text-sm text-slate-600">{timezoneNote}</div>
                {groups.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-slate-600">
                    No appointments found for this range.
                  </div>
                ) : null}
                <div
                  id={HELP_ANCHORS.details}
                  className={`space-y-4 ${getSectionClassName(HELP_ANCHORS.details)}`}
                >
                  {groups.map((group) => (
                  <details
                    key={group.group_key}
                    className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                    open
                  >
                    <summary className="cursor-pointer select-none px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
                      <div className="text-base font-semibold text-slate-800">{group.group_label}</div>
                      <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                        {statusOptions.map((status) => (
                          <span
                            key={`${group.group_key}-${status.value}`}
                            className="rounded-full border border-slate-200 bg-white px-2 py-1"
                          >
                            {status.label}: {group.counts?.[status.value] ?? 0}
                          </span>
                        ))}
                      </div>
                    </summary>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold">Start time</th>
                            <th className="px-4 py-3 text-left font-semibold">Status</th>
                            {withScopeColumns ? (
                              <>
                                <th className="px-4 py-3 text-left font-semibold">Business</th>
                                <th className="px-4 py-3 text-left font-semibold">Salon</th>
                              </>
                            ) : null}
                            <th className="px-4 py-3 text-left font-semibold">Employee</th>
                            <th className="px-4 py-3 text-left font-semibold">Client</th>
                            <th className="px-4 py-3 text-left font-semibold">Service summary</th>
                            <th className="px-4 py-3 text-left font-semibold">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(group.appointments || []).map((appt) => {
                            const rowTimezone = appt?.timezone || anchorTimezone;
                            return (
                              <tr key={appt.appointment_id} className="border-t border-slate-100">
                                <td className="px-4 py-3 text-slate-700">
                                  {formatInTimeZone(
                                    appt.start_at,
                                    rowTimezone,
                                    {
                                      year: "numeric",
                                      month: "short",
                                      day: "2-digit",
                                      hour: "numeric",
                                      minute: "2-digit",
                                    },
                                    "en-US"
                                  )}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {formatStatusLabel(appt.status, statusOptions)}
                                </td>
                                {withScopeColumns ? (
                                  <>
                                    <td className="px-4 py-3 text-slate-700">
                                      {formatEntityLabelWithId(appt.business_id, appt.business_name)}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">
                                      {formatEntityLabelWithId(appt.salon_id, appt.salon_name)}
                                    </td>
                                  </>
                                ) : null}
                                <td className="px-4 py-3 text-slate-700">{appt.employee_name || "--"}</td>
                                <td className="px-4 py-3 text-slate-700">{appt.client_name || "--"}</td>
                                <td className="px-4 py-3 text-slate-700">{appt.service_summary || "--"}</td>
                                <td className="px-4 py-3 text-slate-700">
                                  {appt.duration_minutes ? `${appt.duration_minutes} min` : "--"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </details>
                  ))}
                </div>
              </div>
            ) : null}

          </div>
        </div>
      </div>
    </div>
  );
}
